import { resizeImage } from '../native/imageTools';
import { getMediaAdapter } from '../native/mediaAdapter';
import type { AlbumSaveResult, CapturedFile } from '../native/mediaContract';
import { deleteMediaWithRecords, putMediaWithRecords } from '../persistence/v2Database';
import { releaseMediaUrls, requestMediaUrl, useMediaUrl } from './mediaUrls';
import { currentInspectorProfile, useInspectorProfile, writeInspectorProfile } from './preferences';

// The inspector's own photo (Owner device feedback 2026-09-14: 「右上角的头像变成可上传的验屋师自己的
// 照片」). ONE photo per device, kept in the app's media store under a fixed key and referenced by the
// device inspector profile (`photoUpdatedAt`). It is shown in every top-bar avatar and on the Inspector
// Profile page. Since 2026-10-19 (Owner decision list item 18) it also prints beside the inspector details
// of a report when Settings › Report branding says so (src/state/reportBranding.ts); it is never part of
// an inspection record or an AI request.
//
// The photo contract (src/native/mediaContract.ts) applies to this lane too: a camera capture has its
// system-album save requested the moment the capture succeeds — before the photo is resized or stored —
// and a library pick is never re-saved. Removing the photo touches this database only.
export const INSPECTOR_AVATAR_MEDIA_KEY = 'inspector-avatar';
// Square crops: 640px for the profile page, 192px for the 44px top-bar avatar on a 3× screen.
const FULL_EDGE = 640;
const THUMB_EDGE = 192;

export type InspectorPhotoLane = 'camera' | 'library';

export type InspectorPhotoOutcome = { kind: 'cancelled' } | { kind: 'saved'; album: AlbumSaveResult | null } | { kind: 'error'; message: string };

function messageOf(raw: unknown): string {
  return raw instanceof Error ? raw.message : String(raw);
}

async function pickFile(lane: InspectorPhotoLane): Promise<{ file: CapturedFile | null; album: AlbumSaveResult | null }> {
  const adapter = getMediaAdapter();
  if (lane === 'camera') {
    const file = await adapter.captureFromCamera();
    if (!file) return { file: null, album: null };
    // ALBUM FIRST (OC §6.1): requested before anything else happens to the photo.
    const album = await adapter.saveToSystemAlbum(file).catch((raw): AlbumSaveResult => ({ state: 'failed', detail: `相册保存失败：${messageOf(raw)}` }));
    return { file, album };
  }
  // ONE photo of the inspector: the picker is opened for a single photo (same rule as the report cover).
  const files = await adapter.pickFromLibrary({ single: true });
  return { file: files[0] ?? null, album: null };
}

export async function setInspectorPhotoAction(lane: InspectorPhotoLane, now: number = Date.now()): Promise<InspectorPhotoOutcome> {
  let picked: { file: CapturedFile | null; album: AlbumSaveResult | null };
  try {
    picked = await pickFile(lane);
  } catch (raw) {
    return { kind: 'error', message: messageOf(raw) };
  }
  if (!picked.file) return { kind: 'cancelled' };
  try {
    const full = await resizeImage(picked.file.blob, { maxEdge: FULL_EDGE, square: true });
    const thumb = await resizeImage(picked.file.blob, { maxEdge: THUMB_EDGE, square: true });
    const fullBlob = full ? full.blob : picked.file.blob;
    const fullType = full ? full.blob.type : picked.file.mimeType;
    await putMediaWithRecords(
      INSPECTOR_AVATAR_MEDIA_KEY,
      { type: fullType, bytes: await fullBlob.arrayBuffer() },
      thumb ? { type: thumb.blob.type, bytes: await thumb.blob.arrayBuffer() } : null
    );
  } catch (raw) {
    return { kind: 'error', message: messageOf(raw) };
  }
  writeInspectorProfile({ ...currentInspectorProfile(), photoUpdatedAt: now });
  releaseMediaUrls([INSPECTOR_AVATAR_MEDIA_KEY]);
  requestMediaUrl(INSPECTOR_AVATAR_MEDIA_KEY, 'thumbnail');
  requestMediaUrl(INSPECTOR_AVATAR_MEDIA_KEY, 'full');
  return { kind: 'saved', album: picked.album };
}

// Removes the app's copy only — there is no album API in this app (OC §6.1.2).
export async function removeInspectorPhotoAction(): Promise<void> {
  await deleteMediaWithRecords(INSPECTOR_AVATAR_MEDIA_KEY);
  writeInspectorProfile({ ...currentInspectorProfile(), photoUpdatedAt: null });
  releaseMediaUrls([INSPECTOR_AVATAR_MEDIA_KEY]);
}

export function useInspectorPhotoUrl(variant: 'full' | 'thumbnail'): string | null {
  const profile = useInspectorProfile();
  return useMediaUrl(profile.photoUpdatedAt !== null ? INSPECTOR_AVATAR_MEDIA_KEY : '', variant);
}
